// Cloudflare Workers AI's Aura TTS models hard-reject input over 2000
// characters (HTTP 413), so these must stay safely under that ceiling.
const TARGET_CHARS = 1500;
const MAX_CHARS = 1900;

const SENTENCE_BOUNDARY = /(?<=[.!?])\s+(?=[A-Z0-9"'“])/g;

function splitIntoSentences(paragraph: string): string[] {
  const parts = paragraph.split(SENTENCE_BOUNDARY).map((s) => s.trim()).filter(Boolean);
  return parts.length > 0 ? parts : [paragraph];
}

function hardSplit(text: string, max: number): string[] {
  const out: string[] = [];
  let rest = text.trim();
  while (rest.length > max) {
    let cut = rest.lastIndexOf(" ", max);
    if (cut <= 0) cut = max;
    out.push(rest.slice(0, cut).trim());
    rest = rest.slice(cut).trim();
  }
  if (rest.length > 0) out.push(rest);
  return out;
}

/**
 * Splits text into stable, ordered chunks for TTS: paragraphs first, then
 * sentences, then a hard word-boundary split as a last resort. Never
 * exceeds MAX_CHARS except for a single unsplittable token run.
 */
export function chunkText(rawText: string): string[] {
  const text = rawText.replace(/\r\n/g, "\n").trim();
  if (!text) return [];

  const paragraphs = text
    .split(/\n\s*\n+/)
    .map((p) => p.replace(/\s+/g, " ").trim())
    .filter(Boolean);

  const chunks: string[] = [];
  let current = "";

  const flush = () => {
    if (current.trim()) chunks.push(current.trim());
    current = "";
  };

  for (const paragraph of paragraphs) {
    const candidateLength = current.length + (current ? 1 : 0) + paragraph.length;

    if (paragraph.length > MAX_CHARS) {
      // Paragraph itself is too big: flush what we have, then split by sentence.
      flush();
      for (const sentence of splitIntoSentences(paragraph)) {
        if (sentence.length > MAX_CHARS) {
          for (const piece of hardSplit(sentence, MAX_CHARS)) chunks.push(piece);
          continue;
        }
        const withSentence = current ? `${current} ${sentence}` : sentence;
        if (withSentence.length > TARGET_CHARS && current) {
          flush();
          current = sentence;
        } else {
          current = withSentence;
        }
      }
      continue;
    }

    if (candidateLength > TARGET_CHARS && current) {
      flush();
      current = paragraph;
    } else {
      current = current ? `${current}\n\n${paragraph}` : paragraph;
    }
  }
  flush();

  return chunks;
}
